// Воронка найма: сколько людей на каждом этапе, сколько доходит до следующего, где они
// застревают и кто их ведёт (сценарий 95).
//
// Полноширинное представление: у воронки нет «выбранной строки», ради которой существует
// правая колонка. Есть восемь ступеней и переходы между ними, и читаются они целиком.
//
// **Числа без имён здесь запрещены.** «На этапе „куплен билет“ висит четверо» — это метрика,
// на которую нельзя нажать: рекрутёр не может ни позвонить ей, ни закрыть её. Поэтому
// застрявшие приезжают поимённо и каждый — ссылкой в свою карточку. Так и сделано на сервере,
// и терять это на клиенте нельзя.
//
// **Подписи величин названы ОДИН раз, в шапке таблицы.** До 04.09.2026 каждая ступень рисовалась
// рядом из пяти подписанных величин: восемь ступеней × пять подписей = сорок повторов подписей
// на одном экране, а «никто ещё не проходил» стояло четырнадцать раз подряд. Человек читает
// такое как список, а не как воронку: чтобы сравнить ступени, ему приходится каждый раз заново
// находить глазами, где тут «побывало», а где «застряло». Таблица называет колонку однажды и
// ставит числа друг под другом — тогда их можно сравнить, не читая.
//
// **Полоса доли у «Сейчас» — есть, и это правка того же дня.** Прежняя редакция файла
// объясняла её отсутствие тем, что ширина, посчитанная из данных, — это «оформление в
// разметке, запрещённое правилом». Довод неверный, и стоил он воронки без формы. Правило
// запрещает ЗАШИТЫЕ размеры — ширину в сантиметрах прямо в разметке, ту, что обязана жить
// токеном темы; ширина полосы
// не размер оформления, а САМА ВЕЛИЧИНА, и то же правило говорит прямым текстом: «величину в
// графике кодирует геометрия — высота столбца, длина полосы». Полоса берётся из дизайн-системы
// (`shared/ui/progress-bar`), плоским цветом, без градиента — как правило и требует.
//
// Проценты перехода при этом остались числами: «62 процента, 31 из 50» отвечает точнее, чем
// полоска, которую человек всё равно переводит в проценты глазами. Полоса нужна там, где
// сравнивают ступени между собой, а не там, где читают одну долю.

import { RiFilter3Line } from '@remixicon/react';
import { Link, useNavigate } from 'react-router';

import { formatDataAsOf, parseMoment } from '@/shared/time/format';
import { Block } from '@/shared/ui/block';
import { DateField } from '@/shared/ui/date-field';
import * as ProgressBar from '@/shared/ui/progress-bar';
import { ScreenState, EmptyState, GridSkeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { Toolbar, ToolbarField } from '@/shared/ui/toolbar';
import { cn } from '@/utils/cn';

import type { FunnelOut, RecruiterLoadOut } from '../api/candidates';
import { useUserNames } from '../components/user-names';
import { useFunnelQuery, useRecruiterLoadQuery } from '../queries';
import type { HiringRoute } from '../route';
import { FILTER_SINCE, FILTER_STUCK, hiringHref, setFilter } from '../route';

/** Пороги зависания, которые заказчик называл сам: неделя и две (тз 5.8.3). */
const ПОРОГИ = ['3', '7', '14', '30'] as const;
const ПОРОГ_ПО_УМОЛЧАНИЮ = 7;

export function FunnelView({
  route,
}: {
  route: HiringRoute;
}) {
  const navigate = useNavigate();
  const { search } = route;

  const since = search.filters[FILTER_SINCE];
  const порогИзАдреса = Number(search.filters[FILTER_STUCK]);
  const порог =
    Number.isFinite(порогИзАдреса) && порогИзАдреса > 0 ? порогИзАдреса : ПОРОГ_ПО_УМОЛЧАНИЮ;

  const воронка = useFunnelQuery(since, порог);
  const рекрутёры = useRecruiterLoadQuery(since);
  const имена = useUserNames();

  function apply(key: string, value: string | undefined) {
    navigate(hiringHref(route.view, setFilter(search, key, value)), { replace: true });
  }

  return (
    <div className='flex h-full min-w-0 flex-1 flex-col'>
      <Toolbar
        actions={
          воронка.data === undefined ? undefined : (
            <ToolbarField label='Срез данных'>
              {formatDataAsOf(parseMoment(воронка.data.generated_at) ?? new Date())}
            </ToolbarField>
          )
        }
      >
        <DateField
          id='funnel-since'
          label='Считать с'
          value={since ?? ''}
          onChange={(value) => apply(FILTER_SINCE, value === '' ? undefined : value)}
          hint='Пусто — по всем кандидатам, сколько их есть.'
        />
        <SelectField
          id='funnel-stuck'
          label='Считать застрявшим после'
          value={String(порог)}
          onChange={(value) => apply(FILTER_STUCK, value === NO_VALUE ? undefined : value)}
          options={ПОРОГИ.map((дней) => ({ value: дней, label: `${дней} дней` }))}
          placeholder='7 дней'
        />
      </Toolbar>

      <div className='min-h-0 flex-1 overflow-y-auto p-5'>
        <ScreenState
          data={воронка.data}
          error={воронка.error}
          subject='воронку найма'
          isEmpty={(data) => data.stages.every((этап) => этап.count === 0 && этап.reached === 0)}
          onRetry={() => void воронка.refetch()}
          loading={<GridSkeleton rows={6} />}
          empty={
            <EmptyState
              icon={RiFilter3Line}
              title='Считать пока нечего'
              illustration='период'
              description='За выбранный период в воронку не попал ни один кандидат. Снимите отбор по дате либо дождитесь первых откликов: воронка считается по тому, что уже произошло.'
            />
          }
        >
          {(данные) => (
            <div className='flex flex-col gap-4'>
              <Block
                title='Этапы воронки'
                note={`«Сейчас» — сколько людей стоит на этапе. «Побывало» — сколько прошло через него за период. «Застряло» — сколько висит дольше ${данные.stuck_days} дней.`}
              >
                <ТаблицаСтупеней данные={данные} />
              </Block>

              <Block
                title='Конверсия между соседними этапами'
                note='Доля тех, кто дошёл до следующего этапа. Считается по людям, побывавшим на исходном, а не по тем, кто стоит там сейчас.'
              >
                {данные.conversions.length === 0 ? (
                  <p className='text-paragraph-sm text-text-sub-600'>
                    Переходов между этапами за период не было: людей ещё никуда не двигали.
                  </p>
                ) : (
                  <div className='flex flex-col'>
                    {данные.conversions.map((переход) => (
                      <div
                        key={`${переход.from_status}-${переход.to_status}`}
                        className='flex flex-wrap items-baseline justify-between gap-2 border-b border-stroke-soft-200 py-2 last:border-b-0'
                      >
                        <span className='text-paragraph-sm text-text-strong-950'>
                          {переход.from_title} → {переход.to_title}
                        </span>
                        {/* Прочерк, а не фраза. Причина, которая гасит сразу несколько
                            величин, называется ОДИН раз под рядом (`ui-views.md`): пока она
                            стояла в каждой строке, «на исходном этапе никого не было»
                            повторялось на пустой воронке по разу на переход — восемь
                            одинаковых фраз подряд, между которыми человек ищет разницу. */}
                        <span className='text-label-sm tabular-nums text-text-strong-950'>
                          {переход.rate == null ? '—' : `${Math.round(переход.rate * 100)}%`}
                          {переход.rate != null && (
                            <span className='ml-2 text-paragraph-xs text-text-sub-600'>
                              {переход.reached_to} из {переход.reached_from}
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                    {данные.conversions.some((переход) => переход.rate == null) && (
                      <p className='pt-3 text-paragraph-xs text-text-sub-600'>
                        Прочерк стоит там, где на исходном этапе за период никого не было: доли от
                        нуля не бывает. Это не ноль — считать было не по чему.
                      </p>
                    )}
                  </div>
                )}
              </Block>

              <Block
                title='Кто застрял'
                note={`Кандидаты, висящие на своём этапе дольше ${данные.stuck_days} дней. Каждый — ссылкой в карточку: число без имён закрыть нельзя.`}
              >
                {данные.stuck_candidates.length === 0 ? (
                  <p className='text-paragraph-sm text-text-sub-600'>
                    Никто не висит дольше порога. Это не «данных нет», а ответ: воронка движется.
                  </p>
                ) : (
                  <ul className='flex flex-col'>
                    {данные.stuck_candidates.map((застрявший) => (
                      <li
                        key={застрявший.candidate_id}
                        className='flex flex-wrap items-baseline justify-between gap-2 border-b border-stroke-soft-200 py-2 last:border-b-0'
                      >
                        <Link
                          to={hiringHref(
                            'candidates',
                            { filters: {}, panels: [] },
                            застрявший.candidate_id,
                          )}
                          className='text-paragraph-sm text-primary-base underline тач:flex тач:min-h-11 тач:items-center'
                        >
                          {[застрявший.last_name, застрявший.first_name]
                            .filter((часть) => часть != null && часть !== '')
                            .join(' ') || 'Имя не заполнено'}
                        </Link>
                        <span className='text-paragraph-xs text-text-sub-600'>
                          {застрявший.status_title} · {дни(застрявший.days)}
                          {имена.имя(застрявший.owner_user_id, застрявший.owner_name) !== null
                            ? ` · ведёт ${имена.имя(застрявший.owner_user_id, застрявший.owner_name)}`
                            : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Block>

              <Block
                title='Нагрузка по рекрутёрам'
                note={
                  имена.справочникДоступен
                    ? 'Сколько кандидатов ведёт каждый, сколько довёл до оформления и за сколько дней.'
                    : 'Имена остальных рекрутёров показывает справочник людей, а он открыт администратору и руководству. Ваши кандидаты видны всегда.'
                }
              >
                {(рекрутёры.data?.items ?? []).length === 0 ? (
                  <p className='text-paragraph-sm text-text-sub-600'>
                    Ни за кем из кандидатов ответственный не закреплён.
                  </p>
                ) : (
                  <ТаблицаРекрутёров строки={рекрутёры.data?.items ?? []} имена={имена} />
                )}
              </Block>

              {данные.note !== undefined && данные.note !== '' && (
                <p className='text-paragraph-xs text-text-sub-600'>{данные.note}</p>
              )}
            </div>
          )}
        </ScreenState>
      </div>
    </div>
  );
}


// ── Таблицы ───────────────────────────────────────────────────────────────────────────────
//
// Общая грамматика у обеих: шапка называет колонку ОДИН раз, числа стоят друг под другом и
// моноширинно, первая колонка — имя строки, по которому в неё и попадают. Заголовок колонки
// — `<th>`, а не жирный текст: так его читает и человек, и чтение с экрана, и сквозная
// проверка (`getByRole('columnheader')`).

/** Общие классы ячеек: одно место на обе таблицы, иначе они разъедутся через неделю. */
const ШАПКА = 'whitespace-nowrap px-2 py-2 text-left text-paragraph-xs font-normal text-text-sub-600';
const ЯЧЕЙКА = 'px-2 py-2.5 text-label-sm tabular-nums text-text-strong-950';

function ТаблицаСтупеней({ данные }: { данные: FunnelOut }) {
  // Полоса меряется САМОЙ ЛЮДНОЙ ступенью, а не суммой по всем. Доля от суммы на восьми
  // ступенях даёт восемь коротких огрызков, между которыми ничего не видно; доля от
  // максимума — это и есть форма воронки, ради которой на неё смотрят.
  const максимум = Math.max(1, ...данные.stages.map((этап) => этап.count));
  const естьНепройденные = данные.stages.some((этап) => этап.avg_days == null);

  return (
    <div className='flex flex-col gap-3'>
      <table className='w-full'>
        <thead>
          <tr className='border-b border-stroke-soft-200'>
            <th scope='col' className={ШАПКА}>Этап</th>
            <th scope='col' className={ШАПКА}>Сейчас</th>
            <th scope='col' className={ШАПКА}>Побывало за период</th>
            <th scope='col' className={ШАПКА}>Застряло дольше {данные.stuck_days} дней</th>
            <th scope='col' className={ШАПКА}>Среднее время на этапе</th>
            <th scope='col' className={ШАПКА}>Дольше всех</th>
          </tr>
        </thead>
        <tbody>
          {данные.stages.map((этап) => (
            <tr key={этап.status} className='border-b border-stroke-soft-200 last:border-b-0'>
              <th scope='row' className='px-2 py-2.5 text-left font-normal'>
                <Link
                  to={hiringHref('candidates', {
                    filters: { status: этап.status },
                    panels: [],
                  })}
                  className='text-label-sm text-primary-base underline тач:flex тач:min-h-11 тач:items-center'
                >
                  {этап.status_title}
                </Link>
              </th>
              <td className={ЯЧЕЙКА}>
                <span className='flex items-center gap-2'>
                  <span className='w-6 shrink-0 text-right'>{этап.count}</span>
                  {/* Полоса не подписана и не нажимается: она повторяет число слева, а не
                      добавляет к нему. Ширину даёт величина, краску — дизайн-система. */}
                  <ProgressBar.Root
                    value={этап.count}
                    max={максимум}
                    aria-hidden='true'
                    className='w-24'
                  />
                </span>
              </td>
              <td className={ЯЧЕЙКА}>{этап.reached}</td>
              <td
                className={cn(
                  ЯЧЕЙКА,
                  этап.stuck > 0 ? 'text-error-base' : 'text-text-sub-600',
                )}
              >
                {этап.stuck}
              </td>
              <td className={ЯЧЕЙКА}>{дни(этап.avg_days)}</td>
              <td className={ЯЧЕЙКА}>{дни(этап.max_days)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {естьНепройденные && (
        // Одна строка вместо четырнадцати одинаковых. Причина, гасящая сразу несколько
        // величин, называется ОДИН раз под рядом (`ui-views.md`) — так же сделано у
        // конверсии ниже.
        <p className='text-paragraph-xs text-text-sub-600'>
          Прочерк во времени стоит там, где этап ещё никто не проходил: считать не по чему.
          Это не ноль — ноль означал бы, что этап проходят мгновенно.
        </p>
      )}
    </div>
  );
}

function ТаблицаРекрутёров({
  строки,
  имена,
}: {
  строки: readonly RecruiterLoadOut[];
  имена: ReturnType<typeof useUserNames>;
}) {
  const естьНедоведённые = строки.some((строка) => строка.avg_days_to_hire == null);

  return (
    <div className='flex flex-col gap-3'>
      <table className='w-full'>
        <thead>
          <tr className='border-b border-stroke-soft-200'>
            <th scope='col' className={ШАПКА}>Рекрутёр</th>
            <th scope='col' className={ШАПКА}>Всего вёл</th>
            <th scope='col' className={ШАПКА}>В работе</th>
            <th scope='col' className={ШАПКА}>Оформлено</th>
            <th scope='col' className={ШАПКА}>Отказов</th>
            <th scope='col' className={ШАПКА}>Среднее время до оформления</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((строка, номер) => (
            <tr
              key={строка.user_id ?? `без-ответственного-${номер}`}
              className='border-b border-stroke-soft-200 last:border-b-0'
            >
              <th scope='row' className='px-2 py-2.5 text-left text-label-sm font-normal text-text-strong-950'>
                {строка.user_id === null || строка.user_id === undefined
                  ? 'Без ответственного'
                  : (имена.имя(строка.user_id, строка.user_name) ?? 'Другой рекрутёр')}
              </th>
              <td className={ЯЧЕЙКА}>{строка.total}</td>
              <td className={ЯЧЕЙКА}>{строка.in_work}</td>
              <td className={ЯЧЕЙКА}>{строка.hired}</td>
              <td className={ЯЧЕЙКА}>{строка.rejected}</td>
              <td className={ЯЧЕЙКА}>
                {дни(строка.avg_days_to_hire)}
                {строка.avg_days_to_hire != null && доля(строка.hired_share) !== undefined && (
                  <span className='ml-2 text-paragraph-xs text-text-sub-600'>
                    {доля(строка.hired_share)}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {естьНедоведённые && (
        <p className='text-paragraph-xs text-text-sub-600'>
          Прочерк — рекрутёр ещё никого не довёл до оформления: среднее считать не по чему.
        </p>
      )}
    </div>
  );
}

/**
 * Дни к показу. `null` — не «ноль дней», а «считать не по чему» (правило 7): ноль означал бы,
 * что люди проходят этап мгновенно, а это другой факт.
 */
function дни(значение: number | null | undefined): string {
  if (значение === null || значение === undefined) {
    return '—';
  }
  const округлено = Math.round(значение * 10) / 10;
  return `${округлено.toString().replace('.', ',')} дн.`;
}

function доля(значение: number | null | undefined): string | undefined {
  if (значение === null || значение === undefined) {
    return undefined;
  }
  return `доводит до оформления ${Math.round(значение * 100)}%`;
}
